import { find } from "lodash";
import React, { useCallback } from "react";
import { useMutation } from "@apollo/client";

import { useAllocationConfig } from "src/contexts/global/WorkspaceContext";
import { useUpdateTeamAndMemberDetails } from "src/hooks/useUpdateTeamAndMemberDetails";
import { useModal } from "src/contexts/withModal";
import eventEmitter, { EVENTS } from "src/services/eventEmitter";
import {
  MEMBERSHIP_EVENTS,
  MEMBERSHIP_CHANGE_TYPES,
} from "src/components/Person/events";
import { removePlaceholderPerson as removePlaceholderPersonMutation } from "src/queries/person.graphql";
import { getFteSumInput } from "src/util/fteUtil";
import { isDemand } from "src/util/directory-customerConfig";
import FulfillVacantRoleModal from "src/components/FulfillVacantRole/FulfillVacantRoleModal";
import { hasAdminOrPowerRole } from "src/services/auth";
import { isSupplyGroup } from "src/util/customerConfig";

const useFulfillVacantRole = ({
  person,
  group,
  user,
  groupTypes,
  allocationProject,
}) => {
  const [, dispatch] = useModal();
  const { vacantRoleLabel = "Vacant role", enableAddMember } =
    useAllocationConfig();
  const updateTeamAndMemberDetails = useUpdateTeamAndMemberDetails();
  const [removePlaceholder] = useMutation(removePlaceholderPersonMutation);

  const isDemandTeam = isDemand(groupTypes, group);
  const isAdminOrPower = hasAdminOrPowerRole(user);

  const canShowFulfillButton =
    group && person.isPlaceholder && isAdminOrPower && enableAddMember;
  const canRemoveVacantRole = person.isPlaceholder && isAdminOrPower;

  const onHideModal = useCallback(() => {
    dispatch({ type: "setModalVisible", isModalVisible: false });
  }, [dispatch]);

  const updateMembers = useCallback(
    async (selectedPerson) => {
      const data = await updateTeamAndMemberDetails({
        person,
        groups: [group],
        fteSumInput: getFteSumInput(group, groupTypes),
      });

      const { directGroups, person: updatedPerson } = data || {};
      const { members: updatedMembers } = directGroups?.[0] || {};

      // Add vacant role
      const people = [{ ...person, ...(updatedPerson || {}) }];

      // Add new member if person selected
      let newMember = null;

      if (selectedPerson) {
        newMember = find(
          updatedMembers,
          (m) => m.aggregateId === selectedPerson.aggregateId
        );

        if (newMember) {
          people.push(newMember);
        }
      }

      // For org chart update
      eventEmitter.emit(EVENTS.UPDATE_PEOPLE_AND_GROUPS, {
        directGroups: data?.directGroups || [],
        people,
      });

      // For team details page TeamMembersPanel update
      eventEmitter.emit(MEMBERSHIP_EVENTS.MEMBERSHIP_CHANGED, {
        type: MEMBERSHIP_CHANGE_TYPES.REMOVE,
        person,
        targetTeamId: group?.id,
      });

      if (newMember) {
        eventEmitter.emit(MEMBERSHIP_EVENTS.MEMBERSHIP_CHANGED, {
          type: MEMBERSHIP_CHANGE_TYPES.ADD,
          person: newMember,
          targetTeamId: group?.id,
        });
      }
    },
    [group, groupTypes, person, updateTeamAndMemberDetails]
  );

  const handleAddMember = useCallback(() => {
    // A person can only have one supply group
    const sourceGroup = find(person.memberOf, (g) =>
      isSupplyGroup(groupTypes, g)
    );

    const modalComponent = (
      <FulfillVacantRoleModal
        isOpen={true}
        person={person}
        isDemandTeam={isDemandTeam}
        targetGroupId={group?.id}
        sourceGroup={sourceGroup}
        allocationProjectId={allocationProject?.id}
        groupTypes={groupTypes}
        onHideModal={onHideModal}
        onPersonSelect={updateMembers}
        vacantRoleLabel={vacantRoleLabel}
      />
    );
    dispatch({ type: "setModalComponent", modalComponent });
  }, [
    person,
    isDemandTeam,
    group,
    allocationProject,
    groupTypes,
    onHideModal,
    updateMembers,
    vacantRoleLabel,
    dispatch,
  ]);

  const handleRemoveVacantRole = useCallback(async () => {
    await removePlaceholder({
      variables: {
        personId: person.aggregateId,
      },
    });

    updateMembers(null);
  }, [person, updateMembers, removePlaceholder]);

  return {
    handleAddMember,
    handleRemoveVacantRole,
    canShowFulfillButton,
    canRemoveVacantRole,
    vacantRoleLabel,
  };
};

export default useFulfillVacantRole;
